import MeshData from "./meshdata";

// GPUQueue.writeBuffer requires both `bufferOffset` and `size` to be
// multiples of 4 bytes (WebGPU spec: "Buffer Offset and Size Alignment").
// Typed arrays whose element size is < 4 bytes (Uint16Array, Uint8Array)
// can legally have a byteLength that is *not* a multiple of 4 - e.g. an
// odd-length Uint16Array index buffer (odd triangle-list index count).
// Writing such data directly throws:
//   OperationError: Number of bytes to write must be a multiple of 4
//
// This helper writes as many bytes as possible directly (fast path, no
// copy) and, only when the remaining tail is not 4-byte aligned, stages
// the final <4 trailing bytes into a zero-padded 4-byte scratch buffer.
// This never reads past the end of the source data and never drops or
// truncates real bytes - the destination GPUBuffer is allocated with a
// size rounded up to a multiple of 4, and the extra pad byte(s) are
// never read by drawIndexed()/drawIndirect() because those are driven
// by the true (unpadded) element/index count, not the buffer's byte size.
export function writeBufferAligned(
  queue: GPUQueue,
  buffer: GPUBuffer,
  bufferOffset: number,
  data: ArrayBuffer,
  dataOffset: number,
  size: number
): void {
  const ALIGN = 4;
  const alignedSize = size - (size % ALIGN);

  if (alignedSize > 0) {
    queue.writeBuffer(buffer, bufferOffset, data, dataOffset, alignedSize);
  }

  const tailBytes = size - alignedSize;
  if (tailBytes === 0) return;

  // Stage the unaligned tail (1-3 bytes) into a zero-padded 4-byte
  // buffer so the write size is legal. The destination GPUBuffer must
  // have been allocated with size rounded up to a multiple of 4 so this
  // padded write stays in bounds.
  const padded = new Uint8Array(ALIGN);
  const tail = new Uint8Array(data, dataOffset + alignedSize, tailBytes);
  padded.set(tail, 0);

  queue.writeBuffer(buffer, bufferOffset + alignedSize, padded.buffer, 0, ALIGN);
}

// Rounds a byte size up to the next multiple of 4, as required for
// GPUBuffer allocations that will be targeted by writeBufferAligned().
export function align4(byteLength: number): number {
  return (byteLength + 3) & ~3;
}

export default class Mesh {
  vertex_buffer: GPUBuffer | null = null;
  index_buffer: GPUBuffer | null = null;
  index_count: number = 0;
  stride_bytes: number = 0;
  skinned: boolean = false;
  // Explicit GPUIndexFormat driven by MeshData.index_format. WebGPU
  // natively supports both "uint16" and "uint32" index buffers (see
  // https://www.w3.org/TR/webgpu/#enumdef-gpuindexformat), so this is no
  // longer hardcoded - it always matches the typed array MeshData
  // actually carries (Uint16Array -> "uint16", Uint32Array -> "uint32").
  index_format: GPUIndexFormat = "uint16";

  build(device: GPUDevice, data: MeshData) {
    // Vertex data is always laid out on a stride_bytes (32 or 64) boundary,
    // which is already a multiple of 4, so vertices.byteLength is safe as-is.
    // We still route through align4()/writeBufferAligned() for consistency
    // and to be defensive against future stride changes.
    this.vertex_buffer = device.createBuffer({
      size: align4(data.vertices.byteLength),
      usage: GPUBufferUsage.VERTEX | GPUBufferUsage.COPY_DST,
    });

    writeBufferAligned(
      device.queue,
      this.vertex_buffer,
      0,
      data.vertices.buffer as ArrayBuffer,
      data.vertices.byteOffset,
      data.vertices.byteLength
    );

    // Index data is either a Uint16Array (2 bytes/element) or a
    // Uint32Array (4 bytes/element) - MeshData.index_format tells us
    // which. A Uint16Array with an odd index count (legal for
    // triangle-list primitives) yields a byteLength that is NOT a
    // multiple of 4, which GPUQueue.writeBuffer rejects; a Uint32Array's
    // byteLength is always a multiple of 4 since each element is already
    // 4 bytes, so this same path stays correct (and a no-op tail) for
    // uint32 indices too. Round the buffer allocation up and stage any
    // unaligned tail via writeBufferAligned() so no index data is lost or
    // corrupted and index_count stays exact.
    this.index_format = data.index_format;

    this.index_buffer = device.createBuffer({
      size: align4(data.indices.byteLength),
      usage: GPUBufferUsage.INDEX | GPUBufferUsage.COPY_DST,
    });

    writeBufferAligned(
      device.queue,
      this.index_buffer,
      0,
      data.indices.buffer as ArrayBuffer,
      data.indices.byteOffset,
      data.indices.byteLength
    );

    this.index_count = data.indices.length;
    this.stride_bytes = data.stride_bytes;
    this.skinned = data.skinned;
  }

  draw(pass: GPURenderPassEncoder) {
    if (!this.vertex_buffer || !this.index_buffer || this.index_count === 0) return;

    pass.setVertexBuffer(0, this.vertex_buffer);
    pass.setIndexBuffer(this.index_buffer, this.index_format);
    pass.drawIndexed(this.index_count, 1, 0, 0, 0);
  }

  draw_instanced(pass: GPURenderPassEncoder, instance_count: number)
  {
    if (!this.vertex_buffer || !this.index_buffer || this.index_count === 0) return;
    pass.setVertexBuffer(0, this.vertex_buffer);
    pass.setIndexBuffer(this.index_buffer, this.index_format);
    pass.drawIndexed(this.index_count, instance_count, 0, 0, 0);
  }
}
